import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTimes,
  faDownload,
  faFileInvoiceDollar,
  faTriangleExclamation,
  faShieldHalved,
} from '@fortawesome/free-solid-svg-icons';

import { useFlashMessage } from '../FlashMessage';
import { getErrorMessage } from '../../utils/errorMessages';
import { dashboardService } from '../../services/dashboardService';
import { fetchHMSEntities } from '../../services/invoicingService';
import {
  getPartnerDeliveriesWithActivity,
  downloadStatementOfAccounts,
} from '../../services/statementService';
import HMSPartnerSearch from '../ui/HMSPartnerSearch';
import './StatementOfAccountsModal.css';

/**
 * Statement of Accounts modal.
 *
 * Modal entry point for the SOA generator (the standalone page at
 * /dashboard/crm/statement-of-accounts still works for direct URL access).
 * Same form, same validation, same backend call - rendered inside an
 * overlay that matches the bulk-upload modal's visual language.
 *
 * Approver-gated: a non-approver who somehow triggers this still sees
 * an access-denied state, and the backend re-checks anyway.
 */

const startOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1);
const endOfMonth = (d) => new Date(d.getFullYear(), d.getMonth() + 1, 0);
const toIsoDate = (d) => {
  if (!d) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const TIMESPAN_PRESETS = [
  { key: 'this_month', label: 'This month' },
  { key: 'last_month', label: 'Last month' },
  { key: 'ytd', label: 'Year to date' },
  { key: 'last_12_months', label: 'Last 12 months' },
  { key: 'all_time', label: 'All time' },
  { key: 'custom', label: 'Custom range' },
];

const presetToRange = (key) => {
  const today = new Date();
  switch (key) {
    case 'this_month':
      return { date_from: toIsoDate(startOfMonth(today)), date_to: toIsoDate(today) };
    case 'last_month': {
      const prev = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      return {
        date_from: toIsoDate(startOfMonth(prev)),
        date_to: toIsoDate(endOfMonth(prev)),
      };
    }
    case 'ytd':
      return {
        date_from: toIsoDate(new Date(today.getFullYear(), 0, 1)),
        date_to: toIsoDate(today),
      };
    case 'all_time':
      return { date_from: '2000-01-01', date_to: toIsoDate(today) };
    case 'last_12_months':
    default: {
      const from = new Date(today);
      from.setDate(from.getDate() - 365);
      return { date_from: toIsoDate(from), date_to: toIsoDate(today) };
    }
  }
};

const StatementOfAccountsModal = ({ isOpen, onClose }) => {
  const { showError, showSuccess } = useFlashMessage();

  const [authChecked, setAuthChecked] = useState(false);
  const [isApprover, setIsApprover] = useState(false);

  // Selected partner is the full profile object so the picker can render
  // the chip with legal_entity_name + trading_short_name without an extra
  // fetch. We only forward `id` to the backend when generating the PDF.
  const [selectedPartner, setSelectedPartner] = useState(null);

  const [entities, setEntities] = useState([]);
  // Said beside the picker when the company list fails (ledger row 124).
  const [entitiesError, setEntitiesError] = useState(null);
  const [hmsEntityId, setHmsEntityId] = useState('');

  const [deliveries, setDeliveries] = useState([]);
  const [deliveriesLoading, setDeliveriesLoading] = useState(false);
  const [deliveryId, setDeliveryId] = useState('');

  const [presetKey, setPresetKey] = useState('last_12_months');
  const initialRange = useMemo(() => presetToRange('last_12_months'), []);
  const [customFrom, setCustomFrom] = useState(initialRange.date_from);
  const [customTo, setCustomTo] = useState(initialRange.date_to);

  const [isDownloading, setIsDownloading] = useState(false);
  const [error, setError] = useState(null);

  // Approver gate. Only run when the modal is opened to avoid hammering
  // /api/auth/me on screens that just import this component.
  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    const check = async () => {
      try {
        const info = await dashboardService.getUserInfo();
        if (!cancelled) setIsApprover(Boolean(info?.is_crm_approver));
      } catch (err) {
        console.error('SOA modal: getUserInfo failed', err);
        if (!cancelled) setIsApprover(false);
      } finally {
        if (!cancelled) setAuthChecked(true);
      }
    };
    check();
    return () => { cancelled = true; };
  }, [isOpen]);

  // Load HMS entities once when first opened.
  useEffect(() => {
    if (!isOpen || !isApprover) return;
    let cancelled = false;
    const loadEntities = async () => {
      try {
        const data = await fetchHMSEntities();
        if (cancelled) return;
        setEntities(Array.isArray(data) ? data : data?.results || []);
      } catch (err) {
        console.error('SOA modal: failed to load HMS entities', err);
        if (!cancelled) setEntitiesError(getErrorMessage(err, 'The list of companies could not be loaded.'));
      }
    };
    loadEntities();
    return () => { cancelled = true; };
  }, [isOpen, isApprover]);

  // Cascading deliveries when the picked partner changes.
  useEffect(() => {
    let cancelled = false;
    const loadDeliveries = async () => {
      if (!selectedPartner?.id) {
        setDeliveries([]);
        setDeliveryId('');
        return;
      }
      setDeliveriesLoading(true);
      try {
        const payload = await getPartnerDeliveriesWithActivity(selectedPartner.id);
        if (cancelled) return;
        setDeliveries(payload?.deliveries || []);
        setDeliveryId('');
      } catch (err) {
        console.error('SOA modal: failed to load deliveries', err);
      } finally {
        if (!cancelled) setDeliveriesLoading(false);
      }
    };
    loadDeliveries();
    return () => { cancelled = true; };
  }, [selectedPartner]);

  // Close on Escape.
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => {
      if (e.key === 'Escape' && !isDownloading) onClose?.();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, isDownloading, onClose]);

  const resetState = useCallback(() => {
    setSelectedPartner(null);
    setHmsEntityId('');
    setDeliveryId('');
    setPresetKey('last_12_months');
    const range = presetToRange('last_12_months');
    setCustomFrom(range.date_from);
    setCustomTo(range.date_to);
    setError(null);
  }, []);

  const handleClose = useCallback(() => {
    if (isDownloading) return;
    resetState();
    onClose?.();
  }, [isDownloading, onClose, resetState]);

  const handlePresetChange = useCallback((key) => {
    setPresetKey(key);
    if (key !== 'custom') {
      const range = presetToRange(key);
      setCustomFrom(range.date_from);
      setCustomTo(range.date_to);
    }
  }, []);

  const handleSubmit = useCallback(async (e) => {
    if (e) e.preventDefault();
    setError(null);
    if (!selectedPartner?.id) {
      setError('Please select a partner.');
      return;
    }
    const range = presetKey === 'custom'
      ? { date_from: customFrom, date_to: customTo }
      : presetToRange(presetKey);
    if (!range.date_from || !range.date_to) {
      setError('Please specify both From and To dates.');
      return;
    }
    if (range.date_from > range.date_to) {
      setError('From date cannot be after To date.');
      return;
    }

    setIsDownloading(true);
    try {
      const payload = {
        partner_id: parseInt(selectedPartner.id, 10),
        date_from: range.date_from,
        date_to: range.date_to,
      };
      if (deliveryId) payload.delivery_id = parseInt(deliveryId, 10);
      if (hmsEntityId) payload.hms_entity_id = parseInt(hmsEntityId, 10);

      const blob = await downloadStatementOfAccounts(payload);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      const partnerName = selectedPartner.legal_entity_name
        || selectedPartner.trading_short_name
        || `partner_${selectedPartner.id}`;
      const safeName = partnerName.replace(/[^A-Za-z0-9_-]+/g, '_').slice(0, 60);
      a.href = url;
      a.download = `SOA_${safeName}_${range.date_from}_${range.date_to}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      showSuccess('Statement of Accounts generated.');
      // Close after a successful download so the user is back on the dashboard.
      resetState();
      onClose?.();
    } catch (err) {
      console.error('SOA modal: download failed', err);
      const message = getErrorMessage(err, 'Failed to generate Statement of Accounts PDF.');
      setError(message);
      showError(message);
    } finally {
      setIsDownloading(false);
    }
  }, [
    selectedPartner, presetKey, customFrom, customTo, deliveryId, hmsEntityId,
    showError, showSuccess, onClose, resetState,
  ]);

  if (!isOpen) return null;

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) handleClose();
  };

  return (
    <div className="soa-modal-overlay" onClick={handleOverlayClick}>
      <div
        className="soa-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="soa-modal-title"
      >
        <div className="soa-modal-header">
          <div className="soa-modal-header-text">
            <FontAwesomeIcon icon={faFileInvoiceDollar} className="soa-modal-header-icon" />
            <h2 id="soa-modal-title">Statement of Accounts</h2>
          </div>
          <button
            type="button"
            className="soa-modal-close"
            onClick={handleClose}
            disabled={isDownloading}
            aria-label="Close"
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <div className="soa-modal-body">
          {!authChecked && (
            <div className="soa-modal-loading">Loading…</div>
          )}

          {authChecked && !isApprover && (
            <div className="soa-modal-denied">
              <FontAwesomeIcon icon={faShieldHalved} className="soa-modal-denied-icon" />
              <h3>Approver access required</h3>
              <p>
                The Statement of Accounts generator is restricted to CRM Approvers.
                Please contact your administrator if you need access.
              </p>
            </div>
          )}

          {authChecked && isApprover && (
            <form onSubmit={handleSubmit} className="soa-modal-form">
              <p className="soa-modal-intro">
                Generate a partner-facing PDF summary of every financial movement in
                the period. Pro-formas are excluded.
              </p>

              <div className="soa-modal-field">
                <label>Partner *</label>
                <HMSPartnerSearch
                  value={selectedPartner}
                  onChange={setSelectedPartner}
                  placeholder="Start typing a partner name…"
                  disabled={isDownloading}
                />
              </div>

              <div className="soa-modal-grid">
                <div className="soa-modal-field">
                  <label htmlFor="soa-modal-entity">IMT Entity</label>
                  <select
                    id="soa-modal-entity"
                    value={hmsEntityId}
                    onChange={(e) => setHmsEntityId(e.target.value)}
                  >
                    <option value="">All entities</option>
                    {entities.map((entity) => (
                      <option key={entity.id} value={entity.id}>
                        {entity.hms_entity_name}
                      </option>
                    ))}
                  </select>
                  {entitiesError && (
                    <span role="alert" className="soa-entities-error" style={{ color: '#b91c1c', fontSize: '0.8rem' }}>
                      {entitiesError} The statement can still cover all entities.
                    </span>
                  )}
                </div>

                <div className="soa-modal-field">
                  <label htmlFor="soa-modal-delivery">Delivery</label>
                  <select
                    id="soa-modal-delivery"
                    value={deliveryId}
                    onChange={(e) => setDeliveryId(e.target.value)}
                    disabled={!selectedPartner || deliveriesLoading}
                  >
                    <option value="">
                      {!selectedPartner
                        ? 'Select a partner first'
                        : deliveriesLoading
                          ? 'Loading deliveries…'
                          : deliveries.length === 0
                            ? 'No deliveries with activity'
                            : 'All deliveries'}
                    </option>
                    {deliveries.map((d) => (
                      <option key={d.id} value={d.id}>
                        {d.display_name || d.delivery_unique_number || `Delivery #${d.id}`}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="soa-modal-field">
                <label htmlFor="soa-modal-preset">Timespan</label>
                <select
                  id="soa-modal-preset"
                  value={presetKey}
                  onChange={(e) => handlePresetChange(e.target.value)}
                >
                  {TIMESPAN_PRESETS.map((p) => (
                    <option key={p.key} value={p.key}>{p.label}</option>
                  ))}
                </select>
              </div>

              {presetKey === 'custom' && (
                <div className="soa-modal-grid">
                  <div className="soa-modal-field">
                    <label htmlFor="soa-modal-from">From</label>
                    <input
                      id="soa-modal-from"
                      type="date"
                      value={customFrom}
                      onChange={(e) => setCustomFrom(e.target.value)}
                    />
                  </div>
                  <div className="soa-modal-field">
                    <label htmlFor="soa-modal-to">To</label>
                    <input
                      id="soa-modal-to"
                      type="date"
                      value={customTo}
                      onChange={(e) => setCustomTo(e.target.value)}
                    />
                  </div>
                </div>
              )}

              {error && (
                <div className="soa-modal-error">
                  <FontAwesomeIcon icon={faTriangleExclamation} />
                  <span>{error}</span>
                </div>
              )}

              <div className="soa-modal-footer">
                <button
                  type="button"
                  className="soa-modal-btn soa-modal-btn-secondary"
                  onClick={handleClose}
                  disabled={isDownloading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="soa-modal-btn soa-modal-btn-primary"
                  disabled={isDownloading || !selectedPartner}
                >
                  <FontAwesomeIcon icon={faDownload} />
                  {isDownloading ? 'Generating…' : 'Generate PDF'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default StatementOfAccountsModal;
